import { CommonModule } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivityDefinition, Bundle, ChargeItemDefinition, Coding } from 'fhir/r4';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatStepperModule, StepperOrientation } from '@angular/material/stepper';
import { BreakpointObserver } from '@angular/cdk/layout';

import { ServiceRequestCodePickerComponent, ServiceRequestSelection } from '../shared/service-request-code-picker/service-request-code-picker.component';
import { MatDialog } from '@angular/material/dialog';
import { AddServiceRequestCodesComponent } from '../add-service-request-codes/add-service-request-codes.component';
import { FormFields, formMetaData, GroupField, IndividualField, SingleCodeField } from '../shared/dynamic-forms.interface2';
import { forkJoin, map, of, switchMap, take, Observable, startWith } from 'rxjs';
import { FormFieldsSelectDataService } from '../shared/form-fields-select-data.service';
import { DynamicFormsV2Component } from "../shared/dynamic-forms-v2/dynamic-forms-v2.component";
import { ResourceDataReviewComponent } from "../shared/resource-data-review/resource-data-review.component";
import { ErrorService } from '../shared/error.service';
import { InfoDialogService } from '../shared/info-dialog/info-dialog.service';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { ServiceRequestCodeOption, ServiceRequestCodeOptionWithStatus, UtilityService } from '../shared/utility.service';
import { HttpClient } from '@angular/common/http';
import { LoaderComponent } from '../loader/loader.component';
import { AddSupplyDeliveryComponent } from '../add-supply-delivery/add-supply-delivery.component';
import { StateService } from '../shared/state.service';
import { ServiceRequestCodePickerDialogComponent } from '../service-request-code-picker-dialog/service-request-code-picker/service-request-code-picker.component';
import { Router } from '@angular/router';

@Component({
    selector: 'app-service-details',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatCardModule,
        MatStepperModule,
        ServiceRequestCodePickerComponent,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatDividerModule,
        DynamicFormsV2Component,
        ResourceDataReviewComponent
    ],
    templateUrl: './service-details.component.html',
    styleUrl: './service-details.component.scss'
})
export class ServiceDetailsComponent {
    private fb = inject(FormBuilder);
    private breakpointObserver = inject(BreakpointObserver);
    itemsRequiredForm?: FormFields[]
    requiredObservationsForm?: FormFields[];
    jugglingEffect: string | null = null;
    readonly stepperOrientation$: Observable<StepperOrientation> = this.breakpointObserver
        .observe('(max-width: 767px)')
        .pipe(
            map(({ matches }) => (matches ? 'vertical' : 'horizontal') as StepperOrientation),
            startWith('horizontal' as StepperOrientation)
        );

    step1Form = this.fb.group({
        category: new FormControl<ServiceRequestSelection['category'] | null>(null, Validators.required),
        code: new FormControl<ServiceRequestSelection['serviceRequestCode'] | null>(null, Validators.required),

    });

    private _itemsArrayTruthSource: any[] = [];
    @Input()
    set itemsArrayTruthSource(value: any[] | null | undefined) {
        this._itemsArrayTruthSource = Array.isArray(value) ? [...value.map(item => ({
            ...item,
            //code may be a codeablecocept or string, normalize to coding 
            items: (item.items?.code && item.items?.display) ? item.items : (item.items?.coding?.[0] || item.items)
        }))] : [];
        this.syncActivityDefinitionFromItems();
    }
    get itemsArrayTruthSource(): any[] {
        return this._itemsArrayTruthSource;
    }

    private _requiredObservationsArrayTruthSource: any[] = [];
    get requiredObservationsArrayTruthSource(): any[] {
        return this._requiredObservationsArrayTruthSource;
    }
    set requiredObservationsArrayTruthSource(value: any[] | null | undefined) {
        this._requiredObservationsArrayTruthSource = Array.isArray(value) ? [...value] : [];
        this.syncActivityDefinitionFromItems();
    }


    @Input() serviceCodeAndCategory?: {
        category: ServiceRequestSelection['category']
        serviceRequestCode: ServiceRequestSelection['serviceRequestCode']
    }
    editServiceCodeAndCategoryMode: boolean = false;
    @Input() existingChargeItemDefinition?: ChargeItemDefinition;
    @Input() existingActivityDefinition?: ActivityDefinition;
    newChargeItemDefinition?: ChargeItemDefinition;

    newActivityDefinition?: ActivityDefinition;


    step3Form = this.fb.group({
        confirm: [true, Validators.requiredTrue],
    });

    chosenAndActiveExistingServiceCode: Coding | null = null;
    formMetaDataForPrice: formMetaData = {
        formName: '',
        formDescription: '',
        submitText: 'Save Pricing Breakdown',
    };
    formFieldsDataService = inject(FormFieldsSelectDataService);
    stateService = inject(StateService);
    defsFromServer: ChargeItemDefinition[] = [];
    // NOTE: itemsArrayTruthSource is the single source of truth for selected items.

    // itemsRequiredExtensionUrl = `${this.backendUrl}/fhir/StructureDefinition/items-required`;
    ngOnInit() {
        if (this.serviceCodeAndCategory) {
            this.step1Form.patchValue({
                category: this.serviceCodeAndCategory.category,
                code: this.serviceCodeAndCategory.serviceRequestCode,
            });
        }

        const stream = this.stateService.orgWideResources.chargeItemDefinitions.pipe(take(1),
            switchMap((defs) => {
                //if the stream has defs, return them as observable, otherwise do a new fetch, tap and updae state service
                if (defs && defs.length > 0) {
                    return of(defs);
                }
                return this.http.get<Bundle>(`${this.backendUrl}/ChargeItemDefinition?status=active`).pipe(
                    take(1),
                    switchMap((bundle) => {
                        const items = (bundle.entry || []).map((e) => e.resource as ChargeItemDefinition);
                        this.stateService.orgWideResources.chargeItemDefinitions.next(items.map((d) => {
                            return {
                                actualResource: d,
                                referenceId: `ChargeItemDefinition/${d.id}`,
                                savedStatus: 'saved'
                            }
                        }));
                        return of(items.map((d) => {
                            return {
                                actualResource: d,
                                referenceId: `ChargeItemDefinition/${d.id}`,
                                savedStatus: 'saved'
                            }
                        }));
                    }),

                );
            }),
            map((items) => items.map(item => item.actualResource))

        );
        stream.subscribe({

            next: (defs) => {
                if (!defs) return;
                this.defsFromServer = defs!;
            },
            error: (err) => {
                console.error('Error fetching ChargeItemDefinitions:', err);
                this.errorService.openandCloseError('Failed to load existing charges from server.');
            }

        })

        this.step1Form.get(['code'])?.valueChanges.subscribe((code) => {
// $#$            // /* alert('code changed'); */
            const coding = (code as ServiceRequestCodeOptionWithStatus)?.code;
            let matchedDef: ChargeItemDefinition | undefined = undefined;
            this.defsFromServer.forEach((def) => {
                console.log('checking def' + JSON.stringify(def));
                const defCode = def.code?.coding?.[0]?.code;
                if (defCode && coding && defCode === coding) {
                    matchedDef = def;
// $#$                    // /* alert('found existing active charge for code ' + JSON.stringify(coding) */);
                }
            });
            if (matchedDef) {
                this.existingChargeItemDefinition = matchedDef;
            } else {
                this.existingChargeItemDefinition = undefined;
            }
        });

        if (this.existingActivityDefinition) {
            //set itesarraytruthsource from existing activity definition - mainly itemsrequired
            const itemsExts = (this.existingActivityDefinition.extension || []).filter(
                (ext: any) => ext?.url === this.itemsRequiredExtensionUrl
            );
            const items = itemsExts.map((ext: any) => {
                const sub = Array.isArray(ext?.extension) ? ext.extension : [];
                const itemExt = sub.find((e: any) => e?.url === 'item');
                const quantityExt = sub.find((e: any) => e?.url === 'quantity');
                return {
                    items: itemExt?.valueCodeableConcept,
                    quantity: quantityExt?.valueQuantity?.value,
                };
            });
            this.itemsArrayTruthSource = items;

            // Optional: load required observation codes for this service (lab test names)
            const reqObsExts = (this.existingActivityDefinition.extension || []).filter(
                (ext: any) => ext?.url === this.requiredObservationsExtensionUrl
            );
            const reqObs = reqObsExts
                .map((ext: any) => ext?.valueCodeableConcept)
                .filter(Boolean)
                .map((cc: any) => (cc?.coding?.[0] ?? cc?.text ?? cc));

            this.requiredObservationsArrayTruthSource = reqObs;

        }

        forkJoin({
            itemsRequiredOptions: this.formFieldsDataService.getFormFieldSelectData('activityDefinition', 'itemsRequired'),
        }).subscribe({
            next: ({ itemsRequiredOptions }) => {
                this.itemsRequiredForm =
                    [
                        {
                            generalProperties: {
                                isArray: true,
                                isGroup: true,
                                fieldApiName: 'itemsRequired',
                                fieldName: 'Items Required',
                                fieldLabel: 'Items Required',
                                groupFieldsHint: 'The quantity unit are automatically taken from the selected item. ',
                                // array-of-groups fits better than IndividualField
                                fieldType: 'IndividualField',
                            },
                            keys: ['items', 'quantity'],
                            groupFields: {
                                items: {
                                    generalProperties: {
                                        isArray: false,
                                        isGroup: false,
                                        fieldApiName: 'items',
                                        fieldName: 'Item',
                                        fieldLabel: 'Item',
                                        fieldType: 'CodeableConceptField',
                                    },
                                    data: itemsRequiredOptions ?? [],
                                },
                                quantity: {
                                    generalProperties: {
                                        isArray: false,
                                        isGroup: false,
                                        fieldApiName: 'quantity',
                                        fieldName: 'Quantity',
                                        fieldLabel: 'Quantity',
                                        fieldType: 'IndividualField',
                                        inputType: 'number',
                                    },
                                },
                            },
                        }] as unknown as FormFields[];

                // attach backend options without fighting the strict field typings
                // (this.itemsRequiredForm as any).groupFields.items.data = itemsRequiredOptions ?? [];
            }


        })

        // Optional required observation codes (lab test names) configuration
        // Uses DynamicFormsV2 backend-search support (ValueSet/$expand?filter=...)
        const observationCodesValueSetExpandUrl =
            'https://hapi.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/observation-codes&_format=json&_count=2000';
        this.formFieldsDataService.getFormFieldSelectData('observation', 'localCode').subscribe({
            next: (g) => {
                this.requiredObservationsForm = [
                    {
                        generalProperties: {
                            isArray: true,
                            isGroup: false,
                            fieldApiName: 'requiredObservations',
                            fieldName: 'Required Observation Codes',
                            fieldLabel: 'Required Observation Codes (Optional)',
                            fieldHint: 'Optional. Configure which lab tests (Observation codes) the performer should fill for this service.',
                            fieldType: 'CodeableConceptField',
                            allowedOthers: true,
                            allowTextForCodeableConcept: true,
                        },
                        data: g,
                    } as any,
                ] as unknown as FormFields[];

            },
            error: () => {
                this.errorService.openandCloseError("an error ocurred while loading options'")
            }
        });
    }


    onEditItemsRequired(event: any): void {
        const items = Array.isArray(event?.itemsRequired) ? event.itemsRequired : (Array.isArray(event) ? event : []);
        this.itemsArrayTruthSource = [...items];


    }

    processValuesFromDynamicForm($event: any): void {
        const items = Array.isArray($event?.itemsRequired) ? $event.itemsRequired : (Array.isArray($event) ? $event : []);
        this.itemsArrayTruthSource = [...items];
    }

    onEditRequiredObservations(event: any): void {
        const list = Array.isArray(event?.requiredObservations)
            ? event.requiredObservations
            : (Array.isArray(event) ? event : []);
        this.requiredObservationsArrayTruthSource = [...list.map((e: any) => this.utilityService.toCodeableConcept(e))];
    }

    processValuesFromRequiredObservationsForm($event: any): void {
// $#$        // /* alert(JSON.stringify($event) */);
        const list = Array.isArray($event?.requiredObservations)
            ? $event.requiredObservations
            : (Array.isArray($event) ? $event : []);
        this.requiredObservationsArrayTruthSource = [...list.map((e: any) => this.utilityService.toCodeableConcept(e))];
        console.log('required observations updated: ' + JSON.stringify(this.requiredObservationsArrayTruthSource));
// $#$        // /* debugger; */
    }
    formMetaDataForItemsRequired: formMetaData = {
        formName: '',
        formDescription: '',
        submitText: 'Save',
    };

    private refreshItemsRequiredOptions(): void {
        this.formFieldsDataService
            .getFormFieldSelectData('activityDefinition', 'itemsRequired')
            .pipe(take(1))
            .subscribe({
                next: (itemsRequiredOptions) => {
                    if (!this.itemsRequiredForm) return;
                    (this.itemsRequiredForm as any)[0].groupFields.items.data = itemsRequiredOptions ?? [];
                    this.itemsRequiredForm = [...this.itemsRequiredForm];
                },
            });
    }

    private normalizeCodeableConcept(value: any): any {
        if (!value) return undefined;
        if (typeof value === 'string') {
            if (value.includes('$#$')) {
                const [code = '', display = '', system = ''] = value.split('$#$');
                return {
                    coding: [
                        {
                            code,
                            display,
                            system,
                        },
                    ],
                    text: display || code,
                };
            }
            return { text: value };
        }
        // If dynamic forms already returns a CodeableConcept-ish object, keep it.
        if (typeof value === 'object') {
            return value;
        }
        return { text: String(value) };
    }
    terminologyServer = inject(terminologyCanonicalUrlBaseToken)
    private buildBaseActivityDefinition(): ActivityDefinition {
        const existing = this.existingActivityDefinition;
        const selected = this.step1Form.value.code as any;

        const base: ActivityDefinition = existing
            ? (structuredClone(existing) as ActivityDefinition)
            : {
                resourceType: 'ActivityDefinition',
                status: 'active',
                kind: 'ServiceRequest',

                useContext: [
                    {
                        code: {
                            system: 'http://terminology.hl7.org/fhir/ValueSet/usage-context-type',
                            code: 'workflow',
                            display: 'Workflow Setting'
                        },
                        valueCodeableConcept:

                        {
                            text: 'completing service request'
                        }

                    }
                ],
                topic: [


                    {
                        coding: [{
                            code: 'items usage computation',
                            display: 'items usage computation',
                            system: `${this.terminologyServer}/ActivityDefinition/Topic`
                        }

                        ],
                        text: 'items usage computation'
                    }
                ]
            };

        // Keep the ActivityDefinition aligned to the chosen service code when available.
        if (selected?.code) {
            (base as any).code = {
                coding: [
                    {
                        code: selected.code,
                        display: selected.display,
                        system: selected.system,
                    },
                ],
                text: selected.display,
            };
            if (!(base as any).description && selected.display) {
                (base as any).description = selected.display;
            }
        }

        if (!(base as any).status) {
            (base as any).status = 'active';
        }
        if (!(base as any).kind) {
            (base as any).kind = 'ServiceRequest';
        }

        return base;
    }

    private readonly itemsUnitSuffixRe = /\(\s*in\s*([^)]+?)\s*\)\s*$/i;

    private extractUnitFromDisplay(display?: string | null): string | undefined {
        if (!display) return undefined;
        return display.match(this.itemsUnitSuffixRe)?.[1]?.trim() || undefined;
    }

    private getConceptMatchKey(concept: any): string {
        const coding = concept?.coding?.[0] ?? concept;
        const system = (coding?.system ?? '').toString().trim().toLowerCase();
        const code = (coding?.code ?? '').toString().trim().toLowerCase();
        if (system && code) return `${system}::${code}`;

        const text = (concept?.text ?? concept?.display ?? concept?.coding?.[0]?.display ?? '').toString().trim().toLowerCase();
        return text;
    }

    private getExistingUnitForItem(itemConcept: any): string | undefined {
        const existing = this.existingActivityDefinition;
        if (!existing) return undefined;

        const targetKey = this.getConceptMatchKey(itemConcept);
        if (!targetKey) return undefined;

        const exts = Array.isArray((existing as any).extension) ? (existing as any).extension : [];
        const itemReqExts = exts.filter((e: any) => e?.url === this.itemsRequiredExtensionUrl);

        for (const ext of itemReqExts) {
            const sub = Array.isArray(ext?.extension) ? ext.extension : [];
            const itemExt = sub.find((e: any) => e?.url === 'item');
            const quantityExt = sub.find((e: any) => e?.url === 'quantity');
            const existingItem = itemExt?.valueCodeableConcept;
            const existingKey = this.getConceptMatchKey(existingItem);

            if (existingKey && existingKey === targetKey) {
                const unit = quantityExt?.valueQuantity?.unit;
                return unit ? unit.toString() : undefined;
            }
        }

        return undefined;
    }

    private stripUnitSuffixFromDisplay(display?: string | null): string | undefined {
        if (!display) return undefined;
        return display.replace(this.itemsUnitSuffixRe, '').trim();
    }

    private syncActivityDefinitionFromItems(): void {
        const items = this.itemsArrayTruthSource;
        const requiredObs = this.requiredObservationsArrayTruthSource;

        const base = this.buildBaseActivityDefinition();
        const existingExts = Array.isArray((base as any).extension) ? (base as any).extension : [];
        const keptExts = existingExts.filter(
            (e: any) => e?.url !== this.itemsRequiredExtensionUrl && e?.url !== this.requiredObservationsExtensionUrl
        );

        const itemsExts = (items || [])
            .map((row: any) => {
                const itemConcept = this.normalizeCodeableConcept(row?.items);
                const quantityRaw = row?.quantity;
                const quantityValue = quantityRaw === '' || quantityRaw === null || quantityRaw === undefined ? undefined : Number(quantityRaw);

                if (!itemConcept && (quantityValue === undefined || Number.isNaN(quantityValue))) return null;

                const ext: any = {
                    url: this.itemsRequiredExtensionUrl,
                    extension: [],
                };

                if (itemConcept) {
                    const cleanedDisplay = this.stripUnitSuffixFromDisplay(itemConcept.display) ?? itemConcept.display;
                    ext.extension.push({
                        url: 'item', valueCodeableConcept: {
                            ...(itemConcept.code && itemConcept.display
                                ? {
                                    coding: [
                                        {
                                            code: itemConcept.code,
                                            display: cleanedDisplay,
                                            system: itemConcept.system || `${this.backendUrl}`

                                        }
                                    ]
                                } : {}),
                            ...(itemConcept.text ? { text: itemConcept.text } : {})
                        }
                    });
                }
                if (quantityValue !== undefined && !Number.isNaN(quantityValue)) {
                    const unitFromDisplay = this.extractUnitFromDisplay(itemConcept.display);
                    ext.extension.push({
                        url: 'quantity', valueQuantity: {
                            value: quantityValue, ...(itemConcept.display ? {
                                unit:
                                    unitFromDisplay ??
                                    this.getExistingUnitForItem(itemConcept) ??
                                    ''
                            } : {})
                        }
                    });
                }
                return ext;
            })
            .filter(Boolean);

        const requiredObsExts = (requiredObs || [])
            .map((row: any) => {
                const concept = this.utilityService.toCodeableConcept(row);
                if (!concept) return null;
                return {
                    url: this.requiredObservationsExtensionUrl,
                    valueCodeableConcept: concept,
                } as any;
            })
            .filter(Boolean);

        (base as any).extension = [...keptExts, ...itemsExts, ...(requiredObsExts as any[])];
        this.newActivityDefinition = base;
    }

    onServicePickerChange($event: ServiceRequestSelection): void {
// $#$        // /* alert(JSON.stringify($event) */);

        this.step1Form.patchValue({
            category: $event.category,
            code: $event.serviceRequestCode,
        });

        // Persist selection so the picker can be hidden and later re-opened in edit mode.
        // this.serviceCodeAndCategory = {
        //     category: $event.category,
        //     serviceRequestCode: $event.serviceRequestCode,
        // };
        // this.editServiceCodeAndCategoryMode = false;


    }

    showServiceRequestCodePicker(event?: MouseEvent): void {
        event?.preventDefault();
        event?.stopPropagation();
        // this.editServiceCodeAndCategoryMode = true;
        const dref = this.dialog.open(ServiceRequestCodePickerDialogComponent, {
            maxWidth: '650px',
            maxHeight: '90vh',
            width: '100%',
            data: {
                requireCategory: false,
            },
        });
        dref.afterClosed().pipe(take(1)).subscribe({
            next: (result: ServiceRequestSelection | undefined) => {
                if (!result) return;
                this.onServicePickerChange(result);
            }
        });
    }
    dialog = inject(MatDialog);
    openNewServiceRequestCodeDialog(): void {
        //use diALOG TO OPEN  #ADDSERVICEREQUESTCODECOMPONENT
        const dref = this.dialog.open(AddServiceRequestCodesComponent, {
            maxWidth: '650px',
            maxHeight: '90vh',
            width: '100%',
            data: {
                requireCategory: false,
            },
        });
        dref.afterClosed().pipe(take(1)).subscribe(() => {
            // this.utilityService.invalidateServiceRequestCodeCaches();
            this.jugglingEffect = '';
            // this.jugglingEffect = null;
            this.infoDialogService.info('Service code added. You can now select it from the list.', 3000);
        });



    }

    addNewSupplyDelivery(): void {
        const dref = this.dialog.open(AddSupplyDeliveryComponent, {
            maxWidth: '650px',
            maxHeight: '90vh',
            width: '100%',
        });

        dref.afterClosed().pipe(take(1)).subscribe({
            next: (created) => {
                if (!created) return;
                this.infoDialogService.info('Material saved. Refreshing list...', 2500);
                this.refreshItemsRequiredOptions();
            },
        });
    }

    isItemsArrayTruthSourceValid(): boolean {
        const rows = this.itemsArrayTruthSource;
        if (!Array.isArray(rows) || rows.length === 0) return false;

        return rows.every((row: any) => {
            const item = row?.items;
            const hasItem =
                !!item &&
                (typeof item === 'string'
                    ? item.trim().length > 0
                    : typeof item === 'object'
                        ? !!(item.text || item.display || item.code || item.coding)
                        : false);

            const q = row?.quantity;
            const hasQuantity = !(q === null || q === undefined || q === '');
            const quantityNumber = Number(q);
            const isNumber = Number.isFinite(quantityNumber) && quantityNumber > 0;

            return hasItem && hasQuantity && isNumber;
        });
    }
    backendUrl = inject(backendEndPointToken);
    itemsRequiredExtensionUrl = `http://hl7.org/fhir/StructureDefinition/items-required`;

    // Optional configuration: which observation codes (lab tests) should be filled when performing this service.
    // Stored on ActivityDefinition as repeating extensions with valueCodeableConcept.
    get requiredObservationsExtensionUrl(): string {
        const base = (this.terminologyServer || '').toString().replace(/\/$/, '');
        return `${base}/fhir/StructureDefinition/required-observations`;
    }
    errorService = inject(ErrorService);
    infoDialogService = inject(InfoDialogService);
    preliminaryPricePartOfBundleToSubmit: any[] = []

    //formdata

    priceFields: FormFields[] = [
        <GroupField>{
            generalProperties: {
                fieldApiName: 'priceComponent',
                fieldName: 'Service Price Components',
                fieldLabel: 'Service Price Components',
                groupFieldsHint: 'Define base price, taxes, surcharges, discounts. etc. The total of these components will form the final price presented to the patient.',
                fieldType: 'IndividualField',
                auth: { read: 'all', write: 'admin' },
                isArray: true,
                isGroup: true
            },
            keys: ['type', 'amount'],
            groupFields: {
                type: <SingleCodeField>{
                    generalProperties: {
                        fieldApiName: 'type',
                        fieldName: 'Price Type',
                        fieldLabel: 'Price Type',
                        fieldType: 'CodeField',
                        auth: { read: 'all', write: 'admin' },
                        validations: [
                            {
                                type: 'default',
                                name: 'required',
                                isFunction: false
                            }
                        ],
                        isArray: false,
                        isGroup: false,
                        value: 'base'
                    },
                    data: ['base', 'surcharge', 'deduction', 'discount', 'tax',]
                },
                amount: <IndividualField>{
                    generalProperties: {
                        fieldApiName: 'amount',
                        fieldName: 'Amount',
                        fieldLabel: 'Amount',
                        fieldPlaceholder: '0.00',
                        fieldHint: 'Price amount',
                        validations: [
                            {
                                type: 'default',
                                name: 'required',
                                isFunction: false
                            }
                        ],
                        auth: { read: 'all', write: 'admin' },
                        isArray: false,
                        isGroup: false,
                        inputType: 'number'
                    }
                },

            }
        },
        <GroupField>{
            generalProperties: {
                fieldApiName: 'costPriceComponent',
                fieldName: 'Professional Service Cost',
                fieldLabel: 'Professional Service Cost',
                groupFieldsHint: 'Internal cost representing the professional effort required to perform this service (doctor, nurse, lab technician, etc). Not visible to the patient ',
                fieldType: 'IndividualField',
                auth: { read: 'all', write: 'admin' },
                isArray: false,

                isGroup: true
            },
            keys: ['amount'],
            groupFields: {

                amount: <IndividualField>{
                    generalProperties: {
                        fieldApiName: 'amount',
                        fieldName: 'Amount',
                        fieldLabel: 'Amount',
                        fieldPlaceholder: '0.00',
                        fieldHint: 'Price amount',
                        auth: { read: 'all', write: 'admin' },
                        validations: [
                            {
                                type: 'default',
                                name: 'required',
                                isFunction: false
                            }
                        ],
                        isArray: false,
                        isGroup: false,
                        inputType: 'number'
                    }
                },
            }
        },
    ]


    isChargeItemDefinitionEntriesAvailable() {
        return Array.isArray(this.preliminaryPricePartOfBundleToSubmit) && this.preliminaryPricePartOfBundleToSubmit.length > 0;

    }



    convertChargeItemDefinitionToFormData(chargeItemDefinition: ChargeItemDefinition): any {
        const formData: any = {};

        const priceComponents = chargeItemDefinition.propertyGroup?.[0]?.priceComponent || [];
        formData.priceComponent = priceComponents.map((pc) => {
            const amountValue = (pc as any)?.amount?.value;
            return {
                type: (pc as any)?.type,
                amount: amountValue ?? null,
            };
        });

        // Cost price is stored in ChargeItemDefinition.extension using the
        // http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost structure.
        const totalCostExts = (chargeItemDefinition.extension || []).filter(
            (ext: any) => ext?.url === 'http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost'
        );

        const pickMoneyFromTotalCost = (ext: any) => {
            const sub = Array.isArray(ext?.extension) ? ext.extension : [];
            const typeExt = sub.find((e: any) => e?.url === 'type');
            const amountExt = sub.find((e: any) => e?.url === 'amount');
            return {
                type: typeExt?.valueCode,
                money: amountExt?.valueMoney,
            };
        };

        const parsed = totalCostExts.map(pickMoneyFromTotalCost);
        const base = parsed.find((p: any) => (p?.type || '').toString().toLowerCase() === 'base') || parsed[0];
        const money = base?.money;

        if (money && typeof money.value === 'number') {
            // Keep both keys for compatibility with existing code paths.
            // priceFields uses `CostpriceComponent`, while submit code currently reads `costPriceComponent`.
            const cost = { amount: money.value };
            // formData.CostpriceComponent = cost;
            formData.costPriceComponent = cost;

            if (money.currency) {
                formData.costPriceComponent.currency = { code: money.currency };
            }
        }
        console.log('🔥 Converted ChargeItemDefinition to form data:', formData);
        return formData;
    }
    onFormSubmit(formData: any): void {
        console.log('📋 Charge Form Submitted:', formData);
        const lc = this.dialog.open(LoaderComponent, {});


        // if (!formData.title) {
        //   this.errorService.openandCloseError('Title is required');
        //   return;
        // }

        if (!formData.priceComponent || formData.priceComponent.length === 0) {
            lc.close();
            this.errorService.openandCloseError('At least one price component is required');
            return;
        }
        // if there is service but CostpriceComponent.amiunt is filled, shw notice and continue

        const costPriceComponents = formData.costPriceComponent;
        const pCext = formData.costPriceComponent ? [
            {
                url: "http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost",
                extension: [
                    {
                        url: "type",
                        valueCode: 'base'
                    },
                    {
                        url: "code",
                        valueCodeableConcept: {
                            text: 'cost price component'
                        }
                    },
                    {
                        url: "amount",
                        valueMoney: {
                            value: parseFloat(costPriceComponents?.amount || '0'),
                            currency: formData.currency?.code || 'NGN'
                        }
                    }

                ]
            }
        ] : null;
        const sellingPriceComponents = formData.priceComponent;
        const sellPriceC = sellingPriceComponents?.map((pc: any) => ({
            type: pc.type,
            code: {
                text: 'selling price component'
            },
            amount: {
                value: parseFloat(pc.amount),
                currency: formData.currency?.code || 'NGN'
            }
        }));
        let newChargeItemDef: ChargeItemDefinition;


        // Build FHIR ChargeItemDefinition resource
        if (!this.existingChargeItemDefinition) {
            newChargeItemDef = {
                resourceType: 'ChargeItemDefinition',

                title: (this.step1Form.value.code as ServiceRequestCodeOptionWithStatus).display || 'Unnamed Charge',
                status: 'active',

                url:
                    `${this.backendUrl}/ChargeItemDefinition/${(this.step1Form.value.code as ServiceRequestCodeOptionWithStatus).code}${new Date().getTime()}`,
                // effectivePeriod: formData.effectivePeriod,

                code: {
                    coding: [{
                        code: (this.step1Form.value.code as ServiceRequestCodeOptionWithStatus).code,
                        display: (this.step1Form.value.code as ServiceRequestCodeOptionWithStatus).display,
                        system: (this.step1Form.value.code as ServiceRequestCodeOptionWithStatus).system
                    }],
                    text: (this.step1Form.value.code as ServiceRequestCodeOptionWithStatus).display,
                }
                ,
                propertyGroup: formData.priceComponent ? [{
                    priceComponent: sellPriceC
                }] : [],


            }
        } else {
            newChargeItemDef = {
                ...this.existingChargeItemDefinition,
                title: (this.step1Form.value.code as ServiceRequestCodeOptionWithStatus).display || this.existingChargeItemDefinition.title || 'Unnamed Charge',
                code: {
                    coding: [{
                        code: (this.step1Form.value.code as ServiceRequestCodeOptionWithStatus).code,
                        display: (this.step1Form.value.code as ServiceRequestCodeOptionWithStatus).display,
                        system: (this.step1Form.value.code as ServiceRequestCodeOptionWithStatus).system
                    }],
                    text: (this.step1Form.value.code as ServiceRequestCodeOptionWithStatus).display,
                },
                propertyGroup: formData.priceComponent ? [{
                    priceComponent: sellPriceC
                }] : [],
            }


        }
        if (pCext) {
            newChargeItemDef.extension = pCext;
        }
        console.log('🔥 FHIR ChargeItemDefinition:', newChargeItemDef);

        // Duplicate check (active) for service-based charges.
        // If editing, ignore the current record by id.
        // this.loading = true;
        this.utilityService.findActiveDuplicateByServiceCode(newChargeItemDef).pipe(take(1)).subscribe({
            next: (matches: ChargeItemDefinition[]) => {
                const others = (matches || []).filter((d: any) => {
                    if (!this.existingChargeItemDefinition) return true;
                    return (d?.id ?? '') !== this.existingChargeItemDefinition.id;
                });

                if (others.length && !this.existingChargeItemDefinition) {
                    const dup = others[0] as any;
                    const dupTitle = (dup?.title || dup?.name || dup?.description || 'an existing charge').toString();
                    lc.close();
                    this.errorService.openandCloseError(
                        `An ACTIVE ChargeItemDefinition already exists for this service code (${newChargeItemDef?.code?.coding?.[0]?.code}). (${dupTitle}). Retire it first or edit the existing one.`
                    );
                    return;
                }

                // Build a single FHIR transaction bundle using the same logic:
                // - always create a new ChargeItemDefinition (POST)
                // - if editing, retire the existing ChargeItemDefinition (PUT)
                this.newChargeItemDefinition = newChargeItemDef;
                console.log('🔥 Prepared ChargeItemDefinition for submission:', newChargeItemDef);
                const entries: NonNullable<Bundle['entry']> = [
                    {
                        request: {
                            method: 'POST',
                            url: 'ChargeItemDefinition',
                        },
                        resource: newChargeItemDef as any,
                    } as any,
                ];

                if (this.existingChargeItemDefinition) {
                    if (!this.existingChargeItemDefinition.id) {
                        lc.close();
                        this.errorService.openandCloseError('Cannot retire existing ChargeItemDefinition: missing id.');
                        return;
                    }

                    entries.push({
                        request: {
                            method: 'PUT',
                            url: `ChargeItemDefinition/${this.existingChargeItemDefinition.id}`,
                        },
                        resource: {
                            ...this.existingChargeItemDefinition,
                            status: 'retired',
                        } as any,
                    } as any);
                }

                this.preliminaryPricePartOfBundleToSubmit = [...entries]

                const transactionBundle: Bundle = {
                    resourceType: 'Bundle',
                    type: 'transaction',
                    entry: entries,
                };

                // this.http
                //   .post<Bundle>(this.backendUrl, transactionBundle, {
                //     headers: {
                //       'Content-Type': 'application/fhir+json',
                //       Accept: 'application/fhir+json',
                //       Prefer: 'return=representation',
                //     },
                //   })
                //   .pipe(take(1))
                //   .subscribe({
                //     next: (response) => {
                //       lc.close();
                //       console.log('✅ Transaction completed:', response);
                //     },
                //     error: (err) => {
                //       lc.close();
                //       console.error('❌ Error submitting transaction bundle:', err);
                //       this.errorService.openandCloseError('Failed to submit. Please try again.');
                //     },
                //   });
            },
            error: (err) => {
                lc.close();
                console.error('❌ Error checking duplicates:', err);
                this.errorService.openandCloseError('Failed to validate duplicates. Please try again.');
            },
        });

        // .subscribe({
        //   next: (response) => {
        //     if (!response) return; // duplicate blocked
        //     console.log('✅ Charge created/updated successfully:', response);
        //     this.stateService.persistOrgWideResource(response, 'saved');

        //     this.snackBar.openFromComponent(SuccessMessageComponent, {
        //       data: { message: this.existingChargeId ? 'Charge updated successfully' : 'Charge created successfully' },
        //       duration: 3000
        //     });

        //     this.router.navigate(['/app/charges']);
        //   },
        //   error: (err) => {
        //     console.error('❌ Error submitting charge:', err);
        //     this.errorService.openandCloseError('Failed to submit charge. Please try again.');
        //   },
        //   complete: () => {
        //     this.loading = false;
        //   }
        // });
    }
    utilityService = inject(UtilityService);
    http = inject(HttpClient);

    saveServiceDetails(): void {
        if (this.step1Form.invalid) {
            this.errorService.openandCloseError('Please select a service code first.');
            return;
        }
        if (!this.isItemsArrayTruthSourceValid()) {
            this.errorService.openandCloseError('Please add valid items required before saving.');
            return;
        }

        // Ensure the ActivityDefinition reflects the latest items.
        this.syncActivityDefinitionFromItems();

        const activity = this.newActivityDefinition;
        if (!activity) {
            this.errorService.openandCloseError('ActivityDefinition is missing. Please re-enter items required.');
            return;
        }

        const entries: NonNullable<Bundle['entry']> = [];

        // Include any pricing bundle entries that were prepared in onFormSubmit.
        if (Array.isArray(this.preliminaryPricePartOfBundleToSubmit) && this.preliminaryPricePartOfBundleToSubmit.length > 0) {
            entries.push(...(this.preliminaryPricePartOfBundleToSubmit as any));
        }

        // Always create a new ActivityDefinition (POST). If editing an existing
        // ActivityDefinition, retire the original by issuing a PUT with status
        // 'retired' so the server keeps the original resource but marks it retired.
        entries.push({
            request: { method: 'POST', url: 'ActivityDefinition' },
            resource: activity as any,
        } as any);

        if (this.existingActivityDefinition) {
            if (!this.existingActivityDefinition.id) {
                this.errorService.openandCloseError('Cannot retire existing ActivityDefinition: missing id.');
                return;
            }

            entries.push({
                request: {
                    method: 'PUT',
                    url: `ActivityDefinition/${this.existingActivityDefinition.id}`,
                },
                resource: {
                    ...this.existingActivityDefinition,
                    status: 'retired',
                } as any,
            } as any);
        }

        if (!entries.length) {
            this.errorService.openandCloseError('Nothing to save.');
            return;
        }

        const lc = this.dialog.open(LoaderComponent, {});

        const transactionBundle: Bundle = {
            resourceType: 'Bundle',
            type: 'transaction',
            entry: entries,
        };

        this.http
            .post<Bundle>(this.backendUrl, transactionBundle, {
                headers: {
                    'Content-Type': 'application/fhir+json',
                    Accept: 'application/fhir+json',
                    Prefer: 'return=representation',
                },
            })
            .pipe(take(1))
            .subscribe({
                next: (response) => {
                    lc.close();
                    if (response) {
                        this.stateService.processOrgWideBundleTransaction(response);

                        // Capture returned resources (when server returns representation).
                        const returned = (response.entry || []).map((e: any) => e?.resource).filter(Boolean);
                        const returnedCharge = returned.find((r: any) => r?.resourceType === 'ChargeItemDefinition') as ChargeItemDefinition | undefined;
                        const returnedActivity = returned.find((r: any) => r?.resourceType === 'ActivityDefinition') as ActivityDefinition | undefined;

                        if (returnedCharge) {
                            this.newChargeItemDefinition = returnedCharge;
                            this.existingChargeItemDefinition = returnedCharge;
                        }
                        if (returnedActivity) {
                            this.newActivityDefinition = returnedActivity;
                            this.existingActivityDefinition = returnedActivity;
                        }
                    }

                    this.infoDialogService.info('Service details saved.', 2500);
                    this.route.navigate(['/app/services-details']);
                },
                error: (err) => {
                    lc.close();
                    console.error('❌ Error submitting service details transaction bundle:', err);
                    this.errorService.openandCloseError('Failed to save service details. Please try again.');
                },
            });
    }
    route = inject(Router)



}
